import { useEffect, useMemo, useState } from "react";
import { Menu } from "@headlessui/react";
import { ArrowDownTrayIcon, CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { ReceiptListItem } from "../lib/receipts";
import { ReceiptCard } from "./ReceiptCard";
import { downloadExport, fetchExportPreview, formatBytes, type ExportFormat } from "../lib/export";

const FORMATS: { key: ExportFormat; label: string }[] = [
  { key: "zip", label: "PDFs als ZIP" },
  { key: "csv", label: "CSV (Excel)" },
  { key: "combined", label: "PDFs + CSV" },
];

export function ReceiptListExport({ receipts }: { receipts: ReceiptListItem[] }) {
  const [selecting, setSelecting] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sizeHint, setSizeHint] = useState<string | null>(null);

  const allSelected = receipts.length > 0 && sel.size === receipts.length;
  const ids = [...sel];
  // Geordnete ID-Liste für die ←/→-Navigation in der Detailansicht (B3): die Reihenfolge
  // dieser Liste wird beim Karten-Klick via Router-State mitgegeben, damit man in der
  // Detailansicht durch genau die Liste blättert, aus der man kam.
  const navIds = useMemo(() => receipts.map((r) => r.id), [receipts]);

  const toggle = (id: string) =>
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(receipts.map((r) => r.id)));

  // Größen-Vorschau bei Auswahländerung
  useEffect(() => {
    if (ids.length === 0) { setSizeHint(null); return; }
    let cancelled = false;
    fetchExportPreview(ids)
      .then((p) => { if (!cancelled) setSizeHint(formatBytes(p.totalBytes)); })
      .catch(() => { if (!cancelled) setSizeHint(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);

  const doExport = async (format: ExportFormat) => {
    setBusy(true); setErr(null);
    try {
      await downloadExport(ids, format);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Export fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        {selecting ? (
          <button onClick={toggleAll} className="text-[13px] text-ios-blue hover:text-ios-blueHover">
            {allSelected ? "Auswahl aufheben" : "Alle auswählen"}
          </button>
        ) : <span />}
        <button
          onClick={() => { setSelecting((s) => !s); if (selecting) setSel(new Set()); }}
          className="text-[13px] text-ios-blue hover:text-ios-blueHover inline-flex items-center gap-1"
        >
          {selecting ? "Fertig" : <><CheckCircleIcon className="w-4 h-4" /> Auswählen</>}
        </button>
      </div>

      <ul className="space-y-2">
        {receipts.map((r) => (
          <li key={r.id} className="flex items-center gap-2">
            {selecting && (
              <input
                type="checkbox"
                checked={sel.has(r.id)}
                onChange={() => toggle(r.id)}
                className="accent-ios-blue w-5 h-5 shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <ReceiptCard receipt={r} navIds={navIds} />
            </div>
          </li>
        ))}
      </ul>

      {selecting && sel.size > 0 && (
        <div className="sticky bottom-3 mt-3 z-10">
          <div className="ios-card shadow-card px-4 py-2.5 flex items-center justify-between gap-3 backdrop-blur-ios bg-ios-card/90">
            <div className="text-[14px]">
              <span className="font-medium">{sel.size}</span> ausgewählt
              {sizeHint && <span className="text-ios-muted"> · ~{sizeHint}</span>}
              {err && <span className="text-ios-red ml-2">{err}</span>}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSel(new Set())} className="text-ios-muted hover:text-ios-ink p-1" title="Auswahl leeren">
                <XMarkIcon className="w-4 h-4" />
              </button>
              <Menu as="div" className="relative">
                <Menu.Button
                  disabled={busy}
                  className="text-[13px] px-3 py-1.5 rounded-ios bg-ios-blue text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" /> {busy ? "Export…" : "Export ▾"}
                </Menu.Button>
                <Menu.Items className="absolute right-0 bottom-full mb-1 w-44 ios-card p-1 shadow-card focus:outline-none">
                  {FORMATS.map((f) => (
                    <Menu.Item key={f.key}>
                      {({ active }) => (
                        <button
                          onClick={() => doExport(f.key)}
                          className={`w-full text-left text-[14px] px-3 py-1.5 rounded ${active ? "bg-ios-bg/60" : ""}`}
                        >
                          {f.label}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Menu>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
